import { useState, useEffect, useCallback } from 'react';
import { profileData } from '../data/mockData';
import { detectLanguage, guessLanguageFromBrowser } from '../utils/locale';

/**
 * Custom hook for managing terminal state. Admin mode removed in v3.1.1.
 */
export const useTerminalState = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [terminalReady, setTerminalReady] = useState(false);
  const [showDecrypt, setShowDecrypt] = useState(false);
  const [input, setInput] = useState('');
  const [output, setOutput] = useState([]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [activeSection, setActiveSection] = useState('home');
  const [isMatrixMode, setIsMatrixMode] = useState(false);
  const [showCTFPanel, setShowCTFPanel] = useState(false);
  const [currentData] = useState(profileData);
  // Seeded synchronously so the first paint is already in the right
  // language, then corrected once the IP country comes back.
  const [language, setLanguage] = useState(guessLanguageFromBrowser);

  // Handle loading complete
  const handleLoadingComplete = useCallback(() => {
    setIsLoading(false);
    setIsTransitioning(true);
    setTimeout(() => setShowDecrypt(true), 100);
    setTimeout(() => {
      setTerminalReady(true);
      setIsTransitioning(false);
    }, 1700);
  }, []);

  // Add command to history
  const addToHistory = useCallback((cmd) => {
    setCommandHistory(prev => [...prev, cmd]);
    setHistoryIndex(-1);
  }, []);

  // Navigate history up
  const historyUp = useCallback(() => {
    if (commandHistory.length > 0) {
      const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(newIndex);
      setInput(commandHistory[newIndex]);
    }
  }, [commandHistory, historyIndex]);

  // Navigate history down
  const historyDown = useCallback(() => {
    if (historyIndex !== -1) {
      const newIndex = Math.min(commandHistory.length - 1, historyIndex + 1);
      setHistoryIndex(newIndex);
      setInput(commandHistory[newIndex]);
    }
  }, [commandHistory, historyIndex]);

  // Resolve the visitor's language from their IP country. Bails out if the
  // component unmounts first, or if they picked a language in the meantime.
  useEffect(() => {
    let cancelled = false;
    detectLanguage().then((lang) => {
      if (!cancelled) setLanguage(lang);
    });
    return () => { cancelled = true; };
  }, []);

  return {
    isLoading,
    isTransitioning,
    terminalReady,
    showDecrypt,
    handleLoadingComplete,
    input,
    setInput,
    output,
    setOutput,
    commandHistory,
    addToHistory,
    historyUp,
    historyDown,
    activeSection,
    setActiveSection,
    isMatrixMode,
    setIsMatrixMode,
    showCTFPanel,
    setShowCTFPanel,
    currentData,
    language,
    setLanguage
  };
};

export default useTerminalState;
